import React, { useState, useCallback, useRef } from 'react';
import CircularSlider from '@fseehawer/react-circular-slider';
import classNames from 'classnames';
import ValueInput from '../../../value-input';
import CircleSliderHandle from './components/circle-slider-handle';

const SNAP_STEP = 10;
const SNAP_THRESHOLD = 2;
const MIN_ANGLE = 0;
const MAX_ANGLE = 359;
const TRACK_SIZE = 4;
const LIGHT_TRACK_COLOR = 'rgba(255, 255, 255, 0.5)';
const DARK_TRACK_COLOR = 'rgba(0, 0, 0, 0.08)';

function snapAngle(value) {
  const nearest = Math.round(value / SNAP_STEP) * SNAP_STEP;
  const diff = Math.abs(value - nearest);

  if (diff <= SNAP_THRESHOLD) {
    return nearest % 360;
  }

  return value;
}

const CircleSlider = ({
  angle,
  size = 60,
  knobSize = 16,
  isDarkTheme = false,
  onDragStart = undefined,
  onDrag = undefined,
  onDragEnd = undefined,
  onClick = undefined,
  onInputChange = undefined,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const lastDragValueRef = useRef(angle);
  const lastInputValueRef = useRef(undefined);

  const handleSliderChange = useCallback(
    (value) => {
      if (!isDragging) return;

      lastDragValueRef.current = value;
      onDrag?.({ value: snapAngle(value) });
    },
    [isDragging, onDrag]
  );

  const handleIsDragging = useCallback(
    (dragging) => {
      setIsDragging(dragging);

      if (dragging) {
        lastDragValueRef.current = angle;
        onDragStart?.({ value: snapAngle(angle) });
      } else {
        onDragEnd?.({ value: snapAngle(lastDragValueRef.current) });
      }
    },
    [angle, onDragEnd, onDragStart]
  );

  const handleMouseDown = useCallback(
    (event) => {
      const target = event.target;
      // Prevent clicks on the label or handle from triggering angle changes
      if (target.closest('.ws-ui-circle-slider__label, .ws-ui-circle-slider__handle')) return;

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const dx = event.clientX - (rect.left + rect.width / 2);
      const dy = event.clientY - (rect.top + rect.height / 2);
      const degrees = (Math.atan2(dx, -dy) * 180) / Math.PI;
      const normalized = Math.round((degrees + 360) % 360);

      onClick?.({ value: Math.min(normalized, MAX_ANGLE) });
    },
    [onClick]
  );

  const handleValueInputChange = useCallback(
    (value) => {
      const numValue = typeof value === 'string' ? parseInt(value, 10) : value;

      // Pressing Enter in ValueInput fires onChange twice — once for the key press and once for the blur it triggers. Skip the duplicate so consumers see a single event.
      if (lastInputValueRef.current === numValue) {
        return;
      }

      lastInputValueRef.current = numValue;
      onInputChange?.({ value: numValue });
    },
    [onInputChange]
  );

  const labelValue = (
    <div className="ws-ui-circle-slider__label">
      <ValueInput
        value={`${angle}°`}
        min={MIN_ANGLE}
        max={MAX_ANGLE}
        supportedUnits={['°']}
        appearance="transparent"
        size="tiny"
        align="center"
        bold
        fullWidth
        borderless
        onChange={handleValueInputChange}
      />
    </div>
  );

  const className = classNames('ws-ui-circle-slider', {
    'ws-ui-circle-slider--dark': isDarkTheme,
    'ws-ui-circle-slider--dragging': isDragging,
  });
  const trackColor = isDarkTheme ? LIGHT_TRACK_COLOR : DARK_TRACK_COLOR;

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ '--size': `${size}px`, '--track-size': `${TRACK_SIZE}px` }}
      onMouseDownCapture={handleMouseDown}
    >
      <CircularSlider
        width={size}
        min={MIN_ANGLE}
        max={MAX_ANGLE}
        value={angle}
        knobSize={knobSize}
        knobDraggable
        trackColor={trackColor}
        trackSize={TRACK_SIZE}
        progressSize={0}
        hideLabelValue
        renderLabelValue={labelValue}
        onChange={handleSliderChange}
        isDragging={handleIsDragging}
        useMouseAdditionalToTouch
      >
        <CircleSliderHandle size={knobSize} />
      </CircularSlider>
    </div>
  );
};

CircleSlider.displayName = 'CircleSlider';

export default CircleSlider;
